/**
 * Bonierstand der Kasse (Modul Küchenbons): was von einem Bon schon an Küche
 * und Schank gegangen ist. Bonieren schickt nur den Unterschied zwischen Bon
 * und Bonierstand – neu Bestelltes positiv, Abbestelltes negativ (Storno-Bon).
 * Rabattzeilen und negative Positionen (Gutscheine) werden nie boniert.
 */
import type { Position } from './warenkorb';

export interface BonierStand {
  artikelId?: number;
  bezeichnung: string;
  menge: number;
}

function schluessel(z: { artikelId?: number; bezeichnung: string }): string {
  return z.artikelId === undefined ? `t:${z.bezeichnung.trim()}` : `a:${String(z.artikelId)}`;
}

function runde(m: number): number {
  return Math.round(m * 1000) / 1000;
}

/** Bestellte Mengen des Bons je Artikel (Rabatte, Gutscheine und Gratis-Minus fallen weg). */
export function bestellt(positionen: readonly Position[]): BonierStand[] {
  const je = new Map<string, BonierStand>();
  for (const p of positionen) {
    if (p.bruttoCent < 0 || p.menge <= 0) {
      continue;
    }
    const k = schluessel(p);
    const alt = je.get(k);
    je.set(k, {
      ...(p.artikelId === undefined ? {} : { artikelId: p.artikelId }),
      bezeichnung: alt?.bezeichnung ?? p.bezeichnung,
      menge: runde((alt?.menge ?? 0) + p.menge),
    });
  }
  return [...je.values()];
}

/** Unterschied Bon − Bonierstand; nur Einträge ungleich 0. */
export function bonierDiff(
  positionen: readonly Position[],
  boniert: readonly BonierStand[],
): BonierStand[] {
  const soll = new Map(bestellt(positionen).map((z) => [schluessel(z), z]));
  const ist = new Map(boniert.map((z) => [schluessel(z), z]));
  const diff: BonierStand[] = [];
  for (const [k, z] of soll) {
    const m = runde(z.menge - (ist.get(k)?.menge ?? 0));
    if (m !== 0) {
      diff.push({ ...z, menge: m });
    }
  }
  for (const [k, z] of ist) {
    if (!soll.has(k) && z.menge !== 0) {
      diff.push({ ...z, menge: -z.menge });
    }
  }
  return diff;
}

/** Bonierstand nach dem Senden eines Unterschieds; Mengen ≤ 0 fallen weg. */
export function verbuche(
  boniert: readonly BonierStand[],
  diff: readonly BonierStand[],
): BonierStand[] {
  const je = new Map(boniert.map((z) => [schluessel(z), { ...z }]));
  for (const d of diff) {
    const k = schluessel(d);
    const alt = je.get(k);
    je.set(k, { ...(alt ?? d), menge: runde((alt?.menge ?? 0) + d.menge) });
  }
  return [...je.values()].filter((z) => z.menge > 0);
}

/**
 * Nach dem Kassieren eines Teils (Rechnung teilen): die bezahlten Mengen sind
 * verzehrt, nicht abbestellt – sie verlassen den Bonierstand ohne Storno.
 */
export function abziehen(
  boniert: readonly BonierStand[],
  bezahlt: readonly Position[],
): BonierStand[] {
  return verbuche(
    boniert,
    bestellt(bezahlt).map((z) => ({ ...z, menge: -z.menge })),
  );
}

/**
 * Nachsende-Liste: Bonierungen, deren Übermittlung an einem Netzfehler scheiterte
 * (Codex-Prüfung Kassen-App, Fund 11/12). Sie bleiben dauerhaft auf dem Gerät und
 * gehen mit derselben Vorgangskennung erneut an den Server – der legt sie dann nur
 * einmal an, auch wenn der erste Versuch doch angekommen war.
 */
export interface OffeneBonierung {
  vorgangId: string;
  tisch?: string;
  bediener?: string;
  notiz?: string;
  positionen: BonierStand[];
}

interface Speicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
}

function nachsendeSchluessel(kassenId: string): string {
  return `bilanx.bonierung.nachsenden.${kassenId}`;
}

/** Zufällige Vorgangskennung (8–64 Zeichen, A–Z, a–z, 0–9 und „-“). */
export function neueVorgangsId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c !== undefined && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function ladeNachsendeListe(speicher: Speicher, kassenId: string): OffeneBonierung[] {
  const roh = speicher.getItem(nachsendeSchluessel(kassenId));
  if (roh === null || roh === '') {
    return [];
  }
  try {
    const d: unknown = JSON.parse(roh);
    return Array.isArray(d)
      ? d.filter(
          (x): x is OffeneBonierung =>
            typeof x === 'object' &&
            x !== null &&
            typeof (x as OffeneBonierung).vorgangId === 'string' &&
            Array.isArray((x as OffeneBonierung).positionen),
        )
      : [];
  } catch {
    return [];
  }
}

export function vormerken(speicher: Speicher, kassenId: string, b: OffeneBonierung): void {
  const liste = ladeNachsendeListe(speicher, kassenId).filter((x) => x.vorgangId !== b.vorgangId);
  speicher.setItem(nachsendeSchluessel(kassenId), JSON.stringify([...liste, b]));
}

function entferne(speicher: Speicher, kassenId: string, vorgangId: string): void {
  const liste = ladeNachsendeListe(speicher, kassenId).filter((x) => x.vorgangId !== vorgangId);
  speicher.setItem(nachsendeSchluessel(kassenId), liste.length === 0 ? '' : JSON.stringify(liste));
}

/**
 * Vorgemerkte Bonierungen der Reihe nach senden. Bei einem Netzfehler bricht der
 * Lauf ab (Reihenfolge bleibt); fachlich abgelehnte (400/422) werden verworfen und
 * gemeldet, weil sie nie angenommen würden.
 */
/**
 * Fachlich abgelehnt und nie annehmbar: nur 400 und 422. Anmelde- (401/403), Last- (429) und
 * Serverfehler (5xx) halten die Nachsendung nur an – der Küchenbon bleibt vorgemerkt (Codex P8).
 */
export function bonierungAbgelehnt(e: unknown): boolean {
  const status = (e as { status?: unknown } | null)?.status;
  return status === 400 || status === 422;
}

/** Ein Nachsendelauf je Kasse zugleich (im Tab; über Tabs per Web Locks, falls vorhanden). */
const nachsendeLaeufe = new Map<string, Promise<unknown>>();

export function nachsenden(
  senden: (b: OffeneBonierung) => Promise<unknown>,
  speicher: Speicher,
  kassenId: string,
  istFachlicherFehler: (e: unknown) => boolean,
): Promise<{ gesendet: number; verworfen: number; offen: number }> {
  const locks = (globalThis.navigator as { locks?: LockManager } | undefined)?.locks;
  const arbeit = () => nachsendenOhneSperre(senden, speicher, kassenId, istFachlicherFehler);
  const mitSperre = () =>
    locks === undefined ? arbeit() : locks.request(`bilanx-bonierung-${kassenId}`, arbeit);
  const vorher = nachsendeLaeufe.get(kassenId) ?? Promise.resolve();
  const lauf = vorher.then(mitSperre, mitSperre);
  nachsendeLaeufe.set(
    kassenId,
    lauf.catch(() => undefined),
  );
  return lauf;
}

async function nachsendenOhneSperre(
  senden: (b: OffeneBonierung) => Promise<unknown>,
  speicher: Speicher,
  kassenId: string,
  istFachlicherFehler: (e: unknown) => boolean,
): Promise<{ gesendet: number; verworfen: number; offen: number }> {
  let gesendet = 0;
  let verworfen = 0;
  for (const b of ladeNachsendeListe(speicher, kassenId)) {
    try {
      await senden(b);
      entferne(speicher, kassenId, b.vorgangId);
      gesendet++;
    } catch (e: unknown) {
      if (!istFachlicherFehler(e)) {
        break;
      }
      entferne(speicher, kassenId, b.vorgangId);
      verworfen++;
    }
  }
  return { gesendet, verworfen, offen: ladeNachsendeListe(speicher, kassenId).length };
}
